import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Modal, StyleSheet, Text, View } from 'react-native';

import { testProps } from '../../../config';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { getSafeAreaSidePadding } from '../../../utils/safeArea';
import { TouchableOpacity } from '../common/Touchable';

interface InviteData {
	name: string;
	username: string;
	table: string;
	table_name: string;
	url: string;
	inviteId: string;
}

interface InviteNotificationModalProps {
	visible: boolean;
	inviteData: InviteData | null;
	onAccept: () => void;
	onReject: () => void;
}

const InviteNotificationModal = ({
	visible,
	inviteData,
	onAccept,
	onReject,
}: InviteNotificationModalProps) => {
	const { t } = useTranslation();
	const { isDesktop } = useDevice();
	const { insets } = useSafeViewport();
	const [isProcessing, setIsProcessing] = useState(false);
	const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null);
	const safeOverlayInsets = getSafeAreaSidePadding(insets, 20);
	console.log('InviteNotificationModal render:', { visible, inviteData });

	if (!inviteData) {
		console.log(' No invite data, returning null');
		return null;
	}

	const handleAccept = () => {
		setIsProcessing(true);
		setActionType('accept');
		setTimeout(() => {
			onAccept();
			setIsProcessing(false);
			setActionType(null);
		}, 300);
	};

	const handleReject = () => {
		setIsProcessing(true);
		setActionType('reject');
		setTimeout(() => {
			onReject();
			setIsProcessing(false);
			setActionType(null);
		}, 300);
	};

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			onRequestClose={onReject}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={[styles.overlay, safeOverlayInsets]}>
				<TouchableOpacity
					activeOpacity={1}
					disabled={isProcessing}
					onPress={onReject}
					style={styles.backdropTapArea}
				/>
				<View style={[styles.popupContainer, { width: isDesktop ? 380 : 300 }]}>
					{/* Content */}
					<View style={styles.content}>
						<Text style={styles.inviterText}>
							<Text style={styles.boldText}>{inviteData.username}</Text>{' '}
							{t('T_SITE_INVITE_INVITED_YOU_TO_ROOM', { defaultValue: 'invited you to room' })}
							<Text style={styles.tableValue}> {inviteData.table_name}</Text>
						</Text>
					</View>

					{/* Buttons */}
					<View style={styles.buttonContainer}>
						<TouchableOpacity
							style={[
								styles.button,
								styles.rejectButton,
								isProcessing && actionType === 'reject' && styles.buttonProcessing,
							]}
							onPress={handleReject}
							disabled={isProcessing}
							activeOpacity={0.7}
							{...testProps('cta-invite-decline')}
						>
							{isProcessing && actionType === 'reject' ? (
								<ActivityIndicator color='#FFF' size='small' />
							) : (
								<Text style={styles.buttonText}>
									{t('T_SITE_DENY', { defaultValue: 'Decline' })}
								</Text>
							)}
						</TouchableOpacity>
						<TouchableOpacity
							style={[
								styles.button,
								styles.acceptButton,
								isProcessing && actionType === 'accept' && styles.buttonProcessing,
							]}
							onPress={handleAccept}
							disabled={isProcessing}
							activeOpacity={0.7}
							{...testProps('cta-invite-accept')}
						>
							{isProcessing && actionType === 'accept' ? (
								<ActivityIndicator color='#FFF' size='small' />
							) : (
								<Text style={styles.buttonText}>
									{t('T_SITE_ACCEPT', { defaultValue: 'Accept' })}
								</Text>
							)}
						</TouchableOpacity>
					</View>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		paddingHorizontal: 20,
	},
	popupContainer: {
		backgroundColor: '#1A0F08',
		borderRadius: 16,
		borderWidth: 2,
		overflow: 'hidden',
		shadowColor: '#FFD700',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.5,
		shadowRadius: 15,
		elevation: 20,
		maxWidth: 400,
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},
	header: {
		backgroundColor: '#8B0000',
		paddingVertical: 12,
		paddingHorizontal: 16,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
	},
	headerIcon: {
		fontSize: 24,
	},
	headerText: {
		color: '#FFD700',
		fontSize: 18,
		fontWeight: 'bold',
	},
	content: {
		padding: 20,
		alignItems: 'center',
	},
	inviterText: {
		color: '#FFF',
		fontSize: 15,
		textAlign: 'center',
		marginBottom: 12,
		lineHeight: 22,
	},
	boldText: {
		fontWeight: 'bold',
		color: '#FFD700',
		fontSize: 16,
	},
	tableValue: {
		color: '#FFD700',
		fontSize: 16,
		fontWeight: 'bold',
	},
	buttonContainer: {
		flexDirection: 'row',
		padding: 12,
		gap: 10,
		backgroundColor: '#0D0705',
	},
	button: {
		flex: 1,
		paddingVertical: 10,
		borderRadius: 8,
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 42,
	},
	acceptButton: {
		backgroundColor: '#28A745',
	},
	rejectButton: {
		backgroundColor: '#DC3545',
	},
	buttonProcessing: {
		opacity: 0.6,
	},
	buttonText: {
		color: '#FFF',
		fontSize: 15,
		fontWeight: 'bold',
	},
});

export default InviteNotificationModal;
